// Create/edit dialog for a product category. Controlled form; on save it
// dispatches saveCatalogItem and closes once the write succeeds. Editing an
// existing category also offers a hard delete (cascades to its products,
// options, and pricing) behind a confirmation modal.
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert,
  FormControlLabel, Switch, Box, CircularProgress,
} from '@mui/material';
import { saveCatalogItem, deleteCategory, clearSaveError } from '../../store/admin-catalog-slice.js';
import ConfirmDialog from './ConfirmDialog.jsx';

const DESCRIPTION_MAX = 280;

const emptyForm = {
  name: '', description: '', is_active: true,
};

const CategoryFormDialog = ({ open, category, onClose }) => {
  const dispatch = useDispatch();
  const { saving, saveError, data } = useSelector((state) => state.adminCatalog);
  const [form, setForm] = useState(emptyForm);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isEdit = Boolean(category);

  useEffect(() => {
    if (open) {
      setForm(category ? { ...emptyForm, ...category } : emptyForm);
      setConfirmOpen(false);
      dispatch(clearSaveError());
    }
  }, [open, category, dispatch]);

  const update = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const setActive = (event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    // New categories land at the end of the board; existing ones keep their
    // current display_order (drag-and-drop is the way to reorder).
    const nextOrder = isEdit
      ? Number(category.display_order) || 0
      : (data.categories.reduce((max, c) => Math.max(max, c.display_order || 0), -1) + 1);
    const result = await dispatch(saveCatalogItem({
      resource: 'categories',
      id: category?.id,
      values: {
        name: form.name.trim(),
        description: form.description?.trim() || null,
        display_order: nextOrder,
        is_active: form.is_active,
      },
    }));
    if (saveCatalogItem.fulfilled.match(result)) onClose();
  };

  const productCount = isEdit
    ? data.products.filter((p) => p.category_id === category.id).length
    : 0;

  const handleDelete = async () => {
    const result = await dispatch(deleteCategory({ id: category.id }));
    setConfirmOpen(false);
    if (deleteCategory.fulfilled.match(result)) onClose();
  };

  const confirmMessage = productCount === 0
    ? `This will permanently delete “${category?.name}”. This cannot be undone.`
    : `This will permanently delete “${category?.name}” and its ${productCount} `
      + `product${productCount === 1 ? '' : 's'} (and their options and pricing). `
      + 'This cannot be undone.';

  const descLength = (form.description || '').length;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogTitle>{isEdit ? 'Edit category' : 'New category'}</DialogTitle>
        <DialogContent sx={{
          display: 'flex', flexDirection: 'column', gap: 2, pt: 2,
        }}
        >
          {saveError && <Alert severity="error">{saveError}</Alert>}
          <TextField label="Name" value={form.name} onChange={update('name')} required autoFocus slotProps={{ htmlInput: { maxLength: 200 } }} />
          <TextField
            label="Description"
            value={form.description || ''}
            onChange={update('description')}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: DESCRIPTION_MAX } }}
            helperText={`${descLength}/${DESCRIPTION_MAX} — shown under the category name on the board.`}
          />
          <FormControlLabel
            control={<Switch checked={form.is_active} onChange={setActive} />}
            label="Active"
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Box>
            {isEdit && (
              <Button color="error" onClick={() => setConfirmOpen(true)} disabled={saving}>
                Delete
              </Button>
            )}
          </Box>
          <Box>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving} sx={{ ml: 1 }}>
              {saving ? <CircularProgress size={22} color="inherit" /> : 'Save'}
            </Button>
          </Box>
        </DialogActions>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete category?"
        message={confirmMessage}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </Dialog>
  );
};

export default CategoryFormDialog;
